import { useState, startTransition } from 'react';
import { IoClose, IoFilter } from 'react-icons/io5';

import { Category, Purpose } from '../types';

type SidebarFilterProps = {
    categories: Category[];
    purposes: Purpose[];
    selectedCategory: string | null;
    selectedPurpose: string | null;
    sortOption: string | null;
    onCategoryChange: (category: string | null) => void;
    onPurposeChange: (purpose: string | null) => void;
    onSortChange: (sort: string | null) => void;
    onClearFilters: () => void;
    className?: string;
};

const SidebarFilter = ({
    categories,
    purposes,
    selectedCategory,
    selectedPurpose,
    sortOption,
    onCategoryChange,
    onPurposeChange,
    onSortChange,
    onClearFilters,
    className = '',
}: SidebarFilterProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleCategoryClick = (category: string | null) => {
        startTransition(() => {
            onCategoryChange(category);
        });
        setIsOpen(false);
    };

    const handlePurposeClick = (purpose: string | null) => {
        startTransition(() => {
            onPurposeChange(purpose);
        });
        setIsOpen(false);
    };

    const handleSortClick = (sort: string | null) => {
        startTransition(() => {
            onSortChange(sort);
        });
        setIsOpen(false);
    };

    const FilterContent = () => (
        <div className="space-y-6">
            {/* Header with Clear All */}
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-accent">Filters</h3>
                <button
                    onClick={() => {
                        onClearFilters();
                        setIsOpen(false);
                    }}
                    className="text-sm text-primary font-semibold hover:text-orange-600 transition-colors"
                >
                    Clear All
                </button>
            </div>

            {/* Categories */}
            <div>
                <h4 className="mb-3 text-sm font-semibold text-accent">Categories</h4>
                <div className="space-y-1">
                    <label className="group flex min-h-10 cursor-pointer items-center gap-3 rounded-lg px-1">
                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${!selectedCategory ? 'bg-primary border-primary' : 'border-gray-300 group-hover:border-primary'}`}>
                            {!selectedCategory && <div className="w-2.5 h-2.5 bg-white rounded-sm" />}
                        </div>
                        <input
                            type="checkbox"
                            className="hidden"
                            checked={!selectedCategory}
                            onChange={() => handleCategoryClick(null)}
                        />
                        <span className={`text-sm ${!selectedCategory ? 'text-primary font-semibold' : 'text-text-secondary group-hover:text-primary'}`}>
                            All Categories
                        </span>
                    </label>
                    {categories.map((category) => (
                        <label key={category.id} className="group flex min-h-10 cursor-pointer items-center gap-3 rounded-lg px-1">
                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${selectedCategory === category.tag ? 'bg-primary border-primary' : 'border-gray-300 group-hover:border-primary'}`}>
                                {selectedCategory === category.tag && <div className="w-2.5 h-2.5 bg-white rounded-sm" />}
                            </div>
                            <input
                                type="checkbox"
                                className="hidden"
                                checked={selectedCategory === category.tag}
                                onChange={() => handleCategoryClick(selectedCategory === category.tag ? null : category.tag)}
                            />
                            <span className={`text-sm capitalize ${selectedCategory === category.tag ? 'text-primary font-semibold' : 'text-text-secondary group-hover:text-primary'}`}>
                                {category.name}
                            </span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Purpose */}
            <div>
                <h4 className="mb-3 text-sm font-semibold text-accent">Shop by Purpose</h4>
                <div className="space-y-1">
                    {purposes.map((purpose) => (
                        <label key={purpose.id} className="group flex min-h-10 cursor-pointer items-center gap-3 rounded-lg px-1">
                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${selectedPurpose === purpose.tag ? 'bg-primary border-primary' : 'border-gray-300 group-hover:border-primary'}`}>
                                {selectedPurpose === purpose.tag && <div className="w-2.5 h-2.5 bg-white rounded-sm" />}
                            </div>
                            <input
                                type="checkbox"
                                className="hidden"
                                checked={selectedPurpose === purpose.tag}
                                onChange={() => handlePurposeClick(selectedPurpose === purpose.tag ? null : purpose.tag)}
                            />
                            <span className={`text-sm capitalize ${selectedPurpose === purpose.tag ? 'text-primary font-semibold' : 'text-text-secondary group-hover:text-primary'}`}>
                                {purpose.name}
                            </span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Price Sort */}
            <div>
                <h4 className="mb-3 text-sm font-semibold text-accent">Sort by Price</h4>
                <div className="space-y-1">
                    <label className="group flex min-h-10 cursor-pointer items-center gap-3 rounded-lg px-1">
                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${sortOption === 'price-asc' ? 'border-primary' : 'border-gray-300 group-hover:border-primary'}`}>
                            {sortOption === 'price-asc' && <div className="w-2.5 h-2.5 bg-primary rounded-full" />}
                        </div>
                        <input
                            type="radio"
                            className="hidden"
                            checked={sortOption === 'price-asc'}
                            onChange={() => handleSortClick('price-asc')}
                        />
                        <span className={`text-sm ${sortOption === 'price-asc' ? 'text-primary font-semibold' : 'text-text-secondary group-hover:text-primary'}`}>
                            Low to High
                        </span>
                    </label>
                    <label className="group flex min-h-10 cursor-pointer items-center gap-3 rounded-lg px-1">
                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${sortOption === 'price-desc' ? 'border-primary' : 'border-gray-300 group-hover:border-primary'}`}>
                            {sortOption === 'price-desc' && <div className="w-2.5 h-2.5 bg-primary rounded-full" />}
                        </div>
                        <input
                            type="radio"
                            className="hidden"
                            checked={sortOption === 'price-desc'}
                            onChange={() => handleSortClick('price-desc')}
                        />
                        <span className={`text-sm ${sortOption === 'price-desc' ? 'text-primary font-semibold' : 'text-text-secondary group-hover:text-primary'}`}>
                            High to Low
                        </span>
                    </label>
                </div>
            </div>
        </div>
    );

    return (
        <>
            {/* Desktop Sidebar */}
            <div className={`hidden w-[238px] flex-shrink-0 lg:block xl:w-60 2xl:w-64 ${className}`}>
                <div className="ui-card sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto p-4 pb-8 custom-scroll xl:p-5">
                    <FilterContent />
                </div>
            </div>

            {/* Mobile Filter Button */}
            <div className="lg:hidden flex justify-end">
                <button
                    onClick={() => setIsOpen(true)}
                    className="ui-button-soft"
                >
                    <IoFilter /> Filters
                </button>
            </div>

            {/* Mobile Drawer */}
            {isOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsOpen(false)} />
                    <div className="ui-bottom-sheet absolute bottom-0 left-0 right-0 max-h-[88vh] overflow-y-auto p-5 pb-[calc(env(safe-area-inset-bottom)+2rem)] animate-slideInRight">
                        <div className="mb-5 flex justify-end">
                            <button onClick={() => setIsOpen(false)} className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-gray-100" aria-label="Close filters">
                                <IoClose size={24} />
                            </button>
                        </div>
                        <FilterContent />
                    </div>
                </div>
            )}
        </>
    );
};

export default SidebarFilter;
